import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Maximize2, Minimize2, RotateCcw } from 'lucide-react';
import { scormService, ScormCmiPayload, ScormAssessment } from '@/services/scorm.service';
import { getApiBaseUrl } from '@/config/api.config';
import ContentSkeleton from './ContentSkeleton';

// Older Safari only has the webkit-prefixed Fullscreen API.
const getFullscreenElement = (): Element | null =>
  document.fullscreenElement || (document as any).webkitFullscreenElement || null;

interface ScormPlayerProps {
  contentItem: any;       // PedagogicalContentItem (type='scorm') with scorm_id + scorm_metadata
  selectedModule?: any;
  onProgressChange?: (progressPercentage: number) => void;
  onContentComplete?: () => void;
}

/**
 * SCORM 1.2 player (proxy + postMessage bridge).
 *
 * The SCO HTML is served through our own origin via the backend proxy
 * (/scorm/content/{packageId}/...), which injects a SCORM API shim into the page.
 * The shim posts CMI back to this component via window.postMessage; we persist it
 * (score -> ScormAssessmentAttempts, completion -> CourseTracking) through
 * /scorm/runtime/commit, and seed resume state on launch.
 */
export default function ScormPlayer({
  contentItem,
  selectedModule,
  onProgressChange,
  onContentComplete,
}: ScormPlayerProps) {
  const { courseId } = useParams<{ courseId: string }>();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState<ScormAssessment | null>(null);
  // Bumping this remounts the iframe → fresh SCO launch (new LMSInitialize) without a full
  // page refresh. Needed for packages that disable their wrapper after one submit.
  const [relaunchNonce, setRelaunchNonce] = useState(0);
  // True once the SCO reports a lesson_location (page navigation) → it's a MULTI-PAGE SCO with
  // the quiz embedded among content pages. Those re-attempt via their own navigation, so we
  // hide the "Try again" button for them (it's only for single-screen quiz leaves).
  const [multiPage, setMultiPage] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  // One launch session = one attempt. Regenerated on each LMSInitialize (SCORM_READY) so the
  // backend can tell two genuine attempts apart even when they share the same score.
  const attemptSessionRef = useRef<string>(`${Date.now()}-0`);
  const launchCountRef = useRef<number>(0);

  // Fullscreen, as in HTMLContentPlayer: the browser's Fullscreen API where it
  // works, else a CSS fallback covering the page (iPhone has no element
  // fullscreen). The iframe stays mounted either way, so the SCO keeps its state.
  const frameContainerRef = useRef<HTMLDivElement | null>(null);
  const fullscreenCheckRef = useRef<number>();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const isExpanded = isFullscreen || isPseudoFullscreen;

  const module_id = selectedModule?._id || selectedModule?.module_id;
  const content_item_id = contentItem?._id;
  const scorm_meta = contentItem?.scorm_metadata || {};
  const scorm_id: string | undefined = contentItem?.scorm_id;
  const s3_html_url: string = contentItem?.media_assets?.s3_html_url || '';

  // The learner content DTO may not include scorm_id/scorm_metadata, but the S3 URL
  // always contains both the package id and the relative path:
  //   .../scorm/{package_id}/{relative/path.html}
  // Derive from scorm_metadata first, then fall back to parsing the S3 URL.
  const s3Match = s3_html_url.match(/\/scorm\/([^/]+)\/([^?]+)/);
  const package_id: string | undefined =
    (scorm_id ? scorm_id.split(':')[0] : undefined) || (s3Match ? s3Match[1] : undefined);
  const launch_href: string | undefined =
    scorm_meta?.launch_href || (s3Match ? s3Match[2] : undefined);

  // Build the proxy URL the iframe loads (served from our API origin so the SCORM
  // API shim is injected and completion can be reported).
  const launch_url = (() => {
    if (!package_id || !launch_href) {
      // eslint-disable-next-line no-console
      console.warn('[ScormPlayer] no proxy path — falling back to S3 (completion will NOT track)', { package_id, launch_href, s3: s3_html_url });
      return s3_html_url;
    }
    const base = getApiBaseUrl().replace(/\/$/, '');
    const [path, hrefQuery] = launch_href.split('?');
    const params = [hrefQuery, (scorm_meta?.parameters || '').replace(/^\?/, '')].filter(Boolean).join('&');
    const url = `${base}/scorm/content/${package_id}/${path}${params ? `?${params}` : ''}`;
    // eslint-disable-next-line no-console
    console.info('[ScormPlayer] launch via proxy:', url, { courseId, module_id, content_item_id });
    return url;
  })();

  // Re-show the skeleton whenever the SCO source changes (switching content items),
  // so each new lesson's iframe gets the loading placeholder until it's ready.
  useEffect(() => {
    setLoading(true);
  }, [launch_url, relaunchNonce]);

  const refreshAssessment = async () => {
    if (!content_item_id) return;
    try {
      setAssessment(await scormService.getAssessment(content_item_id));
    } catch { /* non-blocking */ }
  };

  // Re-launch the SCO in place (reload the iframe → new LMSInitialize → new attempt). Used by
  // the "Try again" control for packages that disable themselves after one submit per launch.
  const handleTryAgain = () => {
    setLoading(true);
    setRelaunchNonce((n) => n + 1);
  };

  // Load any existing assessment result (attempts + score) when the lesson opens.
  useEffect(() => {
    setAssessment(null);
    setMultiPage(false);
    refreshAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content_item_id]);

  const elapsedSessionTime = (): string => {
    const total = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  const toPayload = (cmi: Record<string, string>): ScormCmiPayload => {
    const num = (v?: string) => (v !== undefined && v !== '' ? Number(v) : undefined);
    return {
      lesson_status: cmi['cmi.core.lesson_status'],
      score_raw: num(cmi['cmi.core.score.raw']),
      score_min: num(cmi['cmi.core.score.min']),
      score_max: num(cmi['cmi.core.score.max']),
      session_time: cmi['cmi.core.session_time'] || elapsedSessionTime(),
      lesson_location: cmi['cmi.core.lesson_location'],
      suspend_data: cmi['cmi.suspend_data'],
      exit: cmi['cmi.core.exit'],
      entry: cmi['cmi.core.entry'],
    };
  };

  const persist = async (cmi: Record<string, string>, finished = false, trigger?: string) => {
    if (!courseId || !module_id || !content_item_id) {
      // eslint-disable-next-line no-console
      console.warn('[ScormPlayer] persist skipped — missing ids', { courseId, module_id, content_item_id });
      return;
    }
    const payload = toPayload(cmi);
    // eslint-disable-next-line no-console
    console.info('[ScormPlayer] commit →', payload.lesson_status, 'trigger=', trigger, payload);
    // A commit triggered by lesson_location = page navigation → multi-page SCO (quiz embedded
    // with content). Don't lock those — the overlay would block the whole course.
    if (trigger === 'cmi.core.lesson_location') setMultiPage(true);
    try {
      const result = await scormService.commit({
        course_id: courseId,
        module_id,
        content_item_id,
        scorm_package_id: package_id,
        resource_identifier: scorm_meta?.resource_identifier,
        mastery_score: scorm_meta?.mastery_score,
        attempt_session: attemptSessionRef.current,
        finished,
        trigger,
        cmi: payload,
      });
      const status = (payload.lesson_status || '').toLowerCase();
      // Trust the backend's completion decision: it compares the score against the mastery
      // score when the SCO leaves lesson_status non-terminal (e.g. a pass reported as
      // "incomplete"). Fall back to the raw status if the server didn't return a decision.
      const completed = result?.completed ?? (status === 'completed' || status === 'passed');
      // eslint-disable-next-line no-console
      console.info('[ScormPlayer] commit OK, status =', status, 'completed =', completed);
      if (completed) {
        onProgressChange?.(100);
        onContentComplete?.();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ScormPlayer] commit failed', e);
    }
  };

  // Listen for the SCORM shim's postMessages (commit + ready/resume).
  useEffect(() => {
    // The SCO iframe is served from the SCORM proxy (our API origin). Only accept messages
    // from that origin so a malicious frame can't spoof SCORM events — defense-in-depth with
    // the e.source === our-iframe check below.
    const trustedOrigin = (() => {
      try { return new URL(getApiBaseUrl()).origin; } catch { return window.location.origin; }
    })();
    const onMessage = async (e: MessageEvent) => {
      const d: any = e.data;
      if (!d || !d.__scorm) return;
      if (e.origin !== trustedOrigin) {
        // eslint-disable-next-line no-console
        console.warn('[ScormPlayer] message ignored — untrusted origin', e.origin);
        return;
      }
      // eslint-disable-next-line no-console
      console.info('[ScormPlayer] message from SCO:', d.type, d);
      // Only accept messages from our iframe
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) {
        // eslint-disable-next-line no-console
        console.warn('[ScormPlayer] message ignored — source mismatch');
        return;
      }

      if (d.type === 'SCORM_COMMIT' && d.cmi) {
        await persist(d.cmi as Record<string, string>, d.finished === true, d.trigger);
        await refreshAssessment();
      } else if (d.type === 'SCORM_READY') {
        startTimeRef.current = Date.now();
        // New SCO launch → start a new attempt session.
        launchCountRef.current += 1;
        attemptSessionRef.current = `${Date.now()}-${launchCountRef.current}`;
        // Seed resume state back into the SCO
        try {
          if (content_item_id) {
            const resume = await scormService.getResume(content_item_id);
            const seed: Record<string, string> = {};
            if (resume?.lesson_location) seed['cmi.core.lesson_location'] = resume.lesson_location;
            if (resume?.suspend_data) seed['cmi.suspend_data'] = resume.suspend_data;
            seed['cmi.core.entry'] = resume?.entry || 'ab-initio';
            iframeRef.current?.contentWindow?.postMessage(
              { __scorm: true, type: 'SCORM_RESUME', cmi: seed },
              trustedOrigin
            );
          }
        } catch { /* non-blocking */ }
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, module_id, content_item_id]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      window.clearTimeout(fullscreenCheckRef.current);
      setIsFullscreen(!!getFullscreenElement());
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      window.clearTimeout(fullscreenCheckRef.current);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // CSS fallback: Escape leaves it like real fullscreen, and the page behind is
  // locked so iOS doesn't scroll it under the lesson.
  useEffect(() => {
    if (!isPseudoFullscreen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsPseudoFullscreen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPseudoFullscreen]);

  const toggleFullscreen = async () => {
    const el = frameContainerRef.current as any;
    if (!el) return;
    window.clearTimeout(fullscreenCheckRef.current);

    if (isPseudoFullscreen) {
      setIsPseudoFullscreen(false);
      return;
    }
    try {
      if (getFullscreenElement()) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else await (document as any).webkitExitFullscreen?.();
        return;
      }
      const request = el.requestFullscreen || el.webkitRequestFullscreen;
      const enabled = document.fullscreenEnabled || (document as any).webkitFullscreenEnabled;
      // iPhone: the API is missing or disabled for anything but <video>.
      if (!request || !enabled) {
        setIsPseudoFullscreen(true);
        return;
      }
      await request.call(el);
      // Some mobile WebKit builds accept the call and then do nothing.
      fullscreenCheckRef.current = window.setTimeout(() => {
        if (!getFullscreenElement()) setIsPseudoFullscreen(true);
      }, 400);
    } catch (error) {
      console.error('[ScormPlayer] fullscreen toggle failed', error);
      if (!getFullscreenElement()) setIsPseudoFullscreen(true);
    }
  };

  if (!launch_url) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <AlertCircle className="w-12 h-12 text-gray-300 mb-3" />
        <p className="text-gray-600 font-medium">SCORM content not available</p>
        <p className="text-sm text-gray-400 mt-1">No launch file found for this lesson.</p>
      </div>
    );
  }

  // Only a genuine quiz produces a score; keeps this header off plain content lessons
  // (and off any stale assessment rows that never carried a score).
  const isQuiz = !!assessment && (
    assessment.score_raw != null ||
    assessment.score_percentage != null ||
    (assessment.attempts || []).some((a) => a.score_raw != null)
  );
  // "Completed" is sticky: once ANY attempt passed/completed, keep the label even if a later
  // re-attempt fails (the lesson stays completed server-side via the no-downgrade rule).
  const everCompleted = !!assessment && (
    assessment.passed === true ||
    ['passed', 'completed'].includes((assessment.lesson_status || '').toLowerCase()) ||
    (assessment.attempts || []).some((a) => ['passed', 'completed'].includes(String(a.lesson_status || '').toLowerCase()))
  );
  // "Try again" only for single-leaf quizzes (multi-page SCOs re-attempt via their own
  // navigation, so no button). Shown on every attempt and kept visible even after completion.
  // No lock/disable — the SCO and the backend (one attempt per verdict) handle correctness.
  const showTryAgain = !multiPage && isQuiz;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Top bar (quiz lessons only): attempts, latest score, status + in-place re-launch. */}
      {isQuiz && (
        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2 border-b border-gray-200 bg-white text-sm">
          <div className="flex items-center gap-4 text-gray-700">
            <span>
              Attempts completed:{' '}
              <strong className="text-gray-900">{assessment!.attempt_count}</strong>
            </span>
            {assessment!.score_percentage != null && (
              <span>
                Latest score:{' '}
                <strong className="text-gray-900">{assessment!.score_percentage}%</strong>
              </span>
            )}
            {everCompleted && (
              <span className="inline-flex items-center gap-1 text-blue-700 font-medium">
                <CheckCircle2 className="w-4 h-4" /> Completed
              </span>
            )}
          </div>
          {/* Single-leaf quizzes: re-launch for another attempt. Always shown (even after
              completion); multi-page SCOs use their own navigation instead. */}
          {showTryAgain && (
            <button
              type="button"
              onClick={handleTryAgain}
              className="inline-flex items-center gap-1.5 text-brand-blue-700 font-medium hover:text-brand-blue-900"
            >
              <RotateCcw className="w-4 h-4" /> Try again
            </button>
          )}
        </div>
      )}

      <div
        ref={frameContainerRef}
        className={isExpanded
          ? 'fixed inset-0 z-[70] bg-white pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]'
          : 'flex-1 min-h-0 relative'}
      >
        {loading && (
          <div className="absolute inset-0 z-10 bg-white">
            <ContentSkeleton />
          </div>
        )}
        <iframe
          key={relaunchNonce}
          ref={iframeRef}
          title={contentItem?.title || 'SCORM Content'}
          src={launch_url}
          className="w-full h-full border-0"
          onLoad={() => setLoading(false)}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
        />
        {/* Top-right, like the HTML lesson's button; right-5 keeps it clear
            of the lesson's scrollbar. */}
        <button
          type="button"
          onClick={toggleFullscreen}
          className={`absolute right-5 z-20 bg-black/50 hover:bg-black/70 text-white shadow-lg transition-colors ${
            isExpanded
              ? 'top-[calc(env(safe-area-inset-top)+0.5rem)] sm:top-[calc(env(safe-area-inset-top)+1rem)] p-2 sm:p-3 rounded-lg'
              : 'top-3 p-2.5 rounded-full'
          }`}
          title={isExpanded ? 'Exit Fullscreen' : 'Fullscreen'}
          aria-label={isExpanded ? 'Exit Fullscreen' : 'Fullscreen'}
        >
          {isExpanded ? <Minimize2 className="w-5 h-5 sm:w-6 sm:h-6" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
